import { useId, type ReactNode } from 'react';

/** A reason is required on every money / override / grant write (CP5); three characters minimum. */
export const REASON_MIN = 3;

export function reasonOk(reason: string): boolean {
  return reason.trim().length >= REASON_MIN;
}

interface ReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  /** Shown once the admin has typed and the reason is still too short. */
  showError?: boolean;
}

/** The required reason input. Recorded on the service's audit trail with the acting admin. */
export function ReasonField({
  value, onChange, label = 'Reason (required — recorded on the audit trail)', placeholder = 'e.g. ticket ANY-1423, contract amendment', showError,
}: ReasonFieldProps) {
  const id = useId();
  const invalid = (showError ?? value.length > 0) && !reasonOk(value);
  return (
    <div className="field">
      <label className="field-label t-label" htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        value={value}
        required
        minLength={REASON_MIN}
        maxLength={500}
        aria-invalid={invalid || undefined}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {invalid && <span className="field-hint t-caption note-error">At least {REASON_MIN} characters.</span>}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | null;
  type?: 'text' | 'email' | 'number' | 'url' | 'datetime-local' | 'date' | 'password' | 'time';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  mono?: boolean;
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number | 'any';
}

/** A labelled input. `type="password"` is used for write-only secrets so they are never echoed. */
export function TextField({
  label, value, onChange, hint, error, type = 'text', placeholder, required, disabled, mono, autoComplete, min, max, step,
}: TextFieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label t-label" htmlFor={id}>
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        className={`input${mono ? ' mono' : ''}`}
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete ?? (type === 'password' ? 'new-password' : 'off')}
        aria-invalid={error ? true : undefined}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-hint t-caption note-error">{error}</span>
      ) : hint ? (
        <span className="field-hint t-caption subtle">{hint}</span>
      ) : null}
    </div>
  );
}

export function TextAreaField({
  label, value, onChange, hint, error, rows = 4, mono, placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | null;
  rows?: number;
  mono?: boolean;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label t-label" htmlFor={id}>{label}</label>
      <textarea
        id={id}
        className={`input textarea${mono ? ' mono' : ''}`}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {error ? (
        <span className="field-hint t-caption note-error">{error}</span>
      ) : hint ? (
        <span className="field-hint t-caption subtle">{hint}</span>
      ) : null}
    </div>
  );
}

export function CheckboxField({
  label, checked, onChange, hint, disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="checkbox-row t-label">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span>
        {label}
        {hint && <span className="t-caption subtle" style={{ display: 'block' }}>{hint}</span>}
      </span>
    </label>
  );
}

/**
 * A multi-choice chip group (scopes, apps). Buttons with `aria-pressed`, so the set is keyboard-reachable
 * and every option is visible at once.
 */
export function ChoiceChips({
  label, options, selected, onToggle, disabledOptions = [], hint,
}: {
  label: string;
  options: { value: string; label?: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  disabledOptions?: string[];
  hint?: ReactNode;
}) {
  return (
    <div className="field">
      <span className="field-label t-label">{label}</span>
      <div className="choice-chips" role="group" aria-label={label}>
        {options.map((option) => {
          const on = selected.includes(option.value);
          const disabled = disabledOptions.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              className={`choice-chip${on ? ' is-on' : ''}`}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onToggle(option.value)}
            >
              {option.label ?? option.value}
            </button>
          );
        })}
      </div>
      {hint && <span className="field-hint t-caption subtle">{hint}</span>}
    </div>
  );
}

/** Toggles a value in a list (immutable). */
export function toggleIn(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** Splits a comma / newline separated input into trimmed, non-empty, de-duplicated values. */
export function splitList(text: string): string[] {
  return [...new Set(text.split(/[\n,]/).map((v) => v.trim()).filter(Boolean))];
}
